import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {connect} from 'react-redux';
import VM from 'scratch-vm';

import {detectTutorialId} from './tutorial-from-url';
import log from './log';

import {activateDeck} from '../reducers/cards';
import {
    closeLoadingProject,
    openTipsLibrary,
    openLoadingProject
} from '../reducers/modals';
import {
    getIsLoadingUpload,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';

/* Higher Order Component to get parameters from the URL query string and initialize redux state
 * @param {React.Component} WrappedComponent: component to render
 * @returns {React.Component} component with query parsing behavior
 */
const QueryParserHOC = function (WrappedComponent) {
    class QueryParserComponent extends React.Component {
        constructor (props) {
            super(props);
            const queryParams = queryString.parse(location.search);
            this.tutorialId = detectTutorialId(queryParams);
            this.projectFileUrl = queryParams.project_file;
            this.projectTitle = queryParams.project_title;
            this.remoteProjectUploadRequested = false;
            this.remoteProjectLoadStarted = false;
        }
        componentDidMount () {
            if (this.tutorialId) {
                if (this.tutorialId === 'all') {
                    this.openTutorials();
                } else {
                    this.setActiveCards(this.tutorialId);
                }
            }
            this.requestRemoteProjectLoad();
        }
        componentDidUpdate (prevProps) {
            this.requestRemoteProjectLoad();
            if (
                this.projectFileUrl &&
                this.props.isLoadingUpload &&
                this.props.fontsLoaded &&
                this.props.vmReady &&
                !prevProps.isLoadingUpload &&
                !this.remoteProjectLoadStarted
            ) {
                this.loadRemoteProject();
            }
            if (
                this.projectFileUrl &&
                this.props.isLoadingUpload &&
                !prevProps.fontsLoaded &&
                this.props.fontsLoaded &&
                this.props.vmReady &&
                !this.remoteProjectLoadStarted
            ) {
                this.loadRemoteProject();
            }
            if (
                this.projectFileUrl &&
                this.props.isLoadingUpload &&
                !prevProps.vmReady &&
                this.props.vmReady &&
                this.props.fontsLoaded &&
                !this.remoteProjectLoadStarted
            ) {
                this.loadRemoteProject();
            }
        }
        setActiveCards (tutorialId) {
            this.props.onUpdateReduxDeck(tutorialId);
        }
        openTutorials () {
            this.props.onOpenTipsLibrary();
        }
        requestRemoteProjectLoad () {
            if (this.projectFileUrl && !this.remoteProjectUploadRequested) {
                this.remoteProjectUploadRequested = true;
                this.props.onRequestProjectUpload(this.props.loadingState);
            }
        }
        loadRemoteProject () {
            this.remoteProjectLoadStarted = true;
            this.props.onLoadingStarted();
            let loadingSuccess = false;
            fetch(this.projectFileUrl, {
                cache: 'no-store',
                credentials: 'same-origin'
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch Scratch project file: ${response.status}`);
                    }
                    const contentType = response.headers.get('content-type') || '';
                    if (contentType.includes('text/html') || contentType.includes('application/json')) {
                        throw new Error(`Unexpected project file content-type: ${contentType}`);
                    }
                    return response.arrayBuffer();
                })
                .then(projectData => {
                    const projectBytes = new Uint8Array(projectData);
                    const firstMeaningfulByte = projectBytes.find(byte => (
                        byte !== 0x09 &&
                        byte !== 0x0a &&
                        byte !== 0x0d &&
                        byte !== 0x20
                    ));
                    if (firstMeaningfulByte === 0x3c || firstMeaningfulByte === 0x7b) {
                        throw new Error('Fetched response looks like an error page, not a Scratch project file.');
                    }
                    return this.props.vm.loadProject(projectData);
                })
                .then(() => {
                    if (this.projectTitle) {
                        this.props.onSetProjectTitle(this.projectTitle.replace(/\.sb[23]?$/i, ''));
                    }
                    loadingSuccess = true;
                })
                .catch(error => {
                    log.warn(error);
                    window.alert(`项目文件加载失败：${error.message || '请确认上传文件仍可访问。'}`); // eslint-disable-line no-alert
                })
                .then(() => {
                    if (!loadingSuccess) {
                        this.remoteProjectLoadStarted = false;
                    }
                    this.props.onLoadingFinished(this.props.loadingState, loadingSuccess);
                });
        }
        render () {
            const {
                fontsLoaded, // eslint-disable-line no-unused-vars
                isLoadingUpload, // eslint-disable-line no-unused-vars
                loadingState, // eslint-disable-line no-unused-vars
                onLoadingFinished, // eslint-disable-line no-unused-vars
                onLoadingStarted, // eslint-disable-line no-unused-vars
                onOpenTipsLibrary, // eslint-disable-line no-unused-vars
                onRequestProjectUpload, // eslint-disable-line no-unused-vars
                onSetProjectTitle, // eslint-disable-line no-unused-vars
                onUpdateReduxDeck, // eslint-disable-line no-unused-vars
                vm, // eslint-disable-line no-unused-vars
                vmReady, // eslint-disable-line no-unused-vars
                ...componentProps
            } = this.props;
            return (
                <WrappedComponent
                    {...componentProps}
                />
            );
        }
    }
    QueryParserComponent.propTypes = {
        fontsLoaded: PropTypes.bool,
        isLoadingUpload: PropTypes.bool,
        loadingState: PropTypes.string,
        onLoadingFinished: PropTypes.func,
        onLoadingStarted: PropTypes.func,
        onOpenTipsLibrary: PropTypes.func,
        onRequestProjectUpload: PropTypes.func,
        onSetProjectTitle: PropTypes.func,
        onUpdateReduxDeck: PropTypes.func,
        vm: PropTypes.instanceOf(VM),
        vmReady: PropTypes.bool
    };
    const mapStateToProps = state => ({
        fontsLoaded: state.scratchGui.fontsLoaded,
        isLoadingUpload: getIsLoadingUpload(state.scratchGui.projectState.loadingState),
        loadingState: state.scratchGui.projectState.loadingState,
        vm: state.scratchGui.vm,
        vmReady: Boolean(state.scratchGui.vm && state.scratchGui.vm.initialized)
    });
    const mapDispatchToProps = (dispatch, ownProps) => ({
        onLoadingFinished: (loadingState, success) => {
            // A project opened via ?project_file= should behave like a local upload:
            // load it into the editor without trying to save it back to a remote project service.
            dispatch(onLoadedProject(loadingState, false, success));
            dispatch(closeLoadingProject());
        },
        onLoadingStarted: () => {
            dispatch(openLoadingProject());
        },
        onOpenTipsLibrary: () => {
            dispatch(openTipsLibrary());
        },
        onRequestProjectUpload: loadingState => {
            dispatch(requestProjectUpload(loadingState));
        },
        onSetProjectTitle: title => {
            dispatch(setProjectTitle(title));
        },
        onUpdateReduxDeck: tutorialId => {
            dispatch(activateDeck(tutorialId));
        }
    });
    return connect(
        mapStateToProps,
        mapDispatchToProps
    )(QueryParserComponent);
};

export {
    QueryParserHOC as default
};
